// ============================================================================
// osc.js — Discrete OSC Cue Sender
// ----------------------------------------------------------------------------
// Implements osc(...) cue:
//   osc(addr:voice, pitch:y, amp:size, uid:pt)
//
// • Event-based (snapshot at trigger time)
// • Normalized visual parameters (0–1)
// • uid: and trig: belong to the cue lifecycle; they are not sent
// • No DOM ID leakage
// • Uses existing cue trigger lifecycle (click / playhead / armed)
// ============================================================================

import { sendOSC } from "../system/oscillaOSCClient.js";
import { secs } from "./beats.js";
import {
    applyPrestateBeforeStart,
    armElement,
    needsArming
} from "./animShared.js";

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function clamp01(v) {
    return Math.min(1, Math.max(0, v));
}

// ---------------------------------------------------------------------------
// Visual property sampler (NORMALISED, synthesis-oriented)
// ---------------------------------------------------------------------------
function sampleVisual(el) {
    if (!el || !el.getBoundingClientRect) return null;

    // ---------------------------------------------------------
    // Resolve spatial reference:
    //   1) nearest valid osc frame
    //   2) fallback to visible score window (ALWAYS)
    // ---------------------------------------------------------
    let vpEl = null;

    const frameEl = findOscFrame(el);
    if (frameEl) {
        const r = frameEl.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
            vpEl = frameEl;
        }
    }

    // unconditional fallback
    vpEl = vpEl || window.scoreContainer;
    if (!vpEl) return null;

    const vp = vpEl.getBoundingClientRect();
    if (!vp.width || !vp.height) return null;

    const box = el.getBoundingClientRect();

    // ---------------------------------------------------------
    // Position (screen-space → frame-local → normalised)
    // ---------------------------------------------------------
    const cx = box.left + box.width / 2;
    const cy = box.top + box.height / 2;

    const lx = cx - vp.left;
    const ly = cy - vp.top;

    const x = clamp01(lx / vp.width);
    const y = clamp01(1 - (ly / vp.height)); // invert Y for musical semantics

    // ---------------------------------------------------------
    // Geometry (screen-space, perceptual)
    // ---------------------------------------------------------
    const widthRaw = clamp01(box.width / vp.width);
    const heightRaw = clamp01(box.height / vp.height);

    const width = Math.sqrt(widthRaw);                  // envelope / time
    const height = Math.sqrt(heightRaw);                 // brightness
    const area = Math.pow(widthRaw * heightRaw, 0.25); // density / energy

    let aspect = 0.5;
    if (box.height > 0) {
        const ratio = box.width / box.height;
        aspect = clamp01(ratio / (ratio + 1));
    }

    const size = Math.max(width, height);

    // ---------------------------------------------------------
    // Transform-derived values
    // ---------------------------------------------------------
    let scale = 1;
    let rotation = 0;

    const t = el.getAttribute("transform") || "";

    const sm = t.match(/scale\(\s*([-\d.+eE]+)/);
    if (sm) {
        const s = parseFloat(sm[1]);
        if (Number.isFinite(s)) scale = clamp01(Math.abs(s));
    }

    const rm = t.match(/rotate\(\s*([-\d.+eE]+)/);
    if (rm) {
        const deg = parseFloat(rm[1]);
        if (Number.isFinite(deg)) {
            rotation = ((deg % 360) + 360) % 360 / 360;
        }
    }

    // ---------------------------------------------------------
    // Opacity
    // ---------------------------------------------------------
    let opacity = 1;
    const o = getComputedStyle(el).opacity;
    if (o != null) {
        const v = parseFloat(o);
        if (Number.isFinite(v)) opacity = clamp01(v);
    }

    // ---------------------------------------------------------
    // Fill → numeric (deterministic hash)
    // ---------------------------------------------------------
    let fill = 0;
    const fillStr = getComputedStyle(el).fill || "";
    if (fillStr && fillStr !== "none") {
        let h = 0;
        for (let i = 0; i < fillStr.length; i++) {
            h = ((h << 5) - h) + fillStr.charCodeAt(i);
            h |= 0;
        }
        fill = clamp01(Math.abs(h % 1000) / 1000);
    }

    return {
        x, y,
        width,
        height,
        area,
        aspect,
        size,
        scale,
        rotation,
        opacity,
        fill
    };
}


function findOscFrame(el) {
    let node = el.parentElement;
    while (node) {
        if (
            node.hasAttribute?.("data-osc-frame") ||
            (typeof node.id === "string" && node.id.includes("osc_frame"))
        ) {
            return node;
        }
        node = node.parentElement;
    }
    return null;
}


/**
 * Where an element sits inside its osc frame, in the FRAME'S OWN coordinates:
 * x and y from -1 to 1 about the frame's centre, y up.
 *
 * Not the same thing as the screen position sampleVisual() reports, and the
 * difference is the point. An object triggered as it crosses the playhead is,
 * by definition, at the playhead when it fires: on screen every one of them
 * shares the same x, so screen position says nothing about which object spoke.
 * Measured in the frame, each keeps its own place in the drawing however the
 * view is scrolled, zoomed or turned.
 *
 * Which frame is an authoring decision with real consequences: name a frame
 * OUTSIDE a rotation and the position turns with it; name one inside and it
 * holds still. A star map turning overhead can therefore send either the
 * star's fixed place in the sky or where it has been carried to, by marking
 * one group or the other.
 */
function samplePosInFrame(el) {
    const frame = findOscFrame(el);
    if (!frame?.getScreenCTM || !el?.getScreenCTM) return null;
    let fm, em, bb, fb;
    try {
        fm = frame.getScreenCTM(); em = el.getScreenCTM();
        bb = el.getBBox(); fb = frame.getBBox();
    } catch { return null; }
    if (!fm || !em || !bb || !fb || !(fb.width > 0) || !(fb.height > 0)) return null;
    let inv;
    try { inv = fm.inverse(); } catch { return null; }

    const cx = bb.x + bb.width / 2, cy = bb.y + bb.height / 2;
    const sx = em.a * cx + em.c * cy + em.e;          // the element's centre, on screen
    const sy = em.b * cx + em.d * cy + em.f;
    const px = sx * inv.a + sy * inv.c + inv.e;        // ...and in the frame's own space
    const py = sx * inv.b + sy * inv.d + inv.f;

    const hw = fb.width / 2, hh = fb.height / 2;
    return {
        x: clampPos((px - (fb.x + hw)) / hw),
        y: clampPos(-((py - (fb.y + hh)) / hh)),       // y up, as everything else here reads it
    };
}

function clampPos(v) {
    return Number.isFinite(v) ? Math.min(1, Math.max(-1, v)) : 0;
}


// ---------------------------------------------------------------------------
// OSC sender
// ---------------------------------------------------------------------------

function fmt(v) {
    if (Number.isInteger(v)) return v;           // leave ints alone
    return Number(v.toFixed(3));                 // 0.123456 → 0.123
}

// Centralised OSC sender — delegates to oscillaOSCClient
function sendOSCMessage(payload, options = {}) {
    sendOSC(payload, options);
}

// ---------------------------------------------------------------------------
// Main handler
// ---------------------------------------------------------------------------
export function handleOscCue(ast, el, options = {}) {

    if (!ast || !el) {
        console.warn("[handleOscCue] Missing ast or el", ast, el);
        return;
    }

    // ---------------- helpers ----------------
    // Visual feedback for flash:<scale>. Runs on every fire, so it also shows
    // when rotation carries an element into a stopped playhead. Web Animations
    // outrank inline styles, so this briefly wins over a running fade().
    //
    // ONE animation per element, rewound on each fire, never a new one. A
    // finished animation with fill:none is not "replaceable", so Chrome never
    // takes it away: one per fire piles up thousands of live animations over a
    // performance, and that churn makes a dense patch of sky flash as it crosses.
    function flashElement(target, k, seconds) {
        if (!target?.animate) return;
        const same = target._oscFlash && target._oscFlashK === k && target._oscFlashSecs === seconds;
        if (same) {
            try { target._oscFlash.currentTime = 0; target._oscFlash.play(); return; } catch { /* fall through and rebuild */ }
        }
        try { target._oscFlash?.cancel(); } catch { /* already gone */ }
        // The reference box only has to be set the first time round.
        if (target.style.transformBox !== "fill-box") {
            target.style.transformBox = "fill-box";
            target.style.transformOrigin = "center";
        }
        target._oscFlashK = k;
        target._oscFlashSecs = seconds;
        target._oscFlash = target.animate(
            [
                { transform: "scale(1)", opacity: 1 },
                { transform: `scale(${k})`, opacity: 1, offset: 0.25 },
                { transform: "scale(1)" }
            ],
            { duration: seconds * 1000, easing: "ease-out" }
        );
    }

    function clamp01(x) {
        return Math.min(1, Math.max(0, x));
    }

    function norm(value, min, max) {
        return clamp01((value - min) / (max - min));
    }

    const astArgs = ast.args || [];

    let addr = null;
    let init = null;
    let dur = null;   // note duration in beats; triggers note-off via setTimeout
    let gate = null;  // explicit gate override: 1=note-on, 0=note-off; skips dur scheduling
    // pos:1 — also send where this object is inside its osc frame, so a
    // receiver can place it in space. Two values, x then y, each -1 to 1 about
    // the frame's centre with y up. See samplePosInFrame().
    let sendPos = false;
    let flash = 0;        // flash:<scale> — swell + brighten the element each time it fires
    // args:[…] — the message's arguments exactly, instead of the positional
    // layout below: numbers as they are, rand() drawn each time, the name of
    // a visual source (x, y, size, scale, rotation, opacity, fill, width,
    // height, area, aspect) sampled as it fires, any other word as a string
    let argList = null;
    let flashDur = null;  // flashdur:<beats>, 0.35 s if not given

    const staticParams = {};
    const visualMappings = {};

    // ------------------ parse args ------------------
    for (const a of astArgs) {
        const key = a.type;
        const val = a.value;

        if (key === "addr") addr = String(val).trim();
        else if (key === "uid" || key === "trig") { /* cue lifecycle, not sent */ }
        else if (key === "root") { staticParams.root = Number(val); }
        else if (key === "init") init = val;
        else if (key === "dur") dur = Number(val);
        else if (key === "gate") gate = Number(val) === 0 ? 0 : 1;
        else if (key === "pos") sendPos = !["0", "false", "no", "off"].includes(String(val).toLowerCase());
        else if (key === "flash") flash = Number(val) || 0;
        else if (key === "flashdur") flashDur = Number(val) || null;
        else if (key === "args") argList = Array.isArray(val) ? val : [val];
        else if (val && typeof val === "object" && val.type)
            staticParams[key] = val;
        else if (typeof val === "string")
            visualMappings[key] = val;
        else
            staticParams[key] = val;
    }

    if (!addr) {
        console.error("[osc] addr missing");
        return;
    }

    if (init) applyPrestateBeforeStart(el, init); // TODO: osc.js needs cfg-based prestate like other handlers

    function evalRuntime(val) {
        if (val == null) return val;
        if (typeof val !== "object") return val;

        if (val.type === "irand")
            return Math.floor(val.min + Math.random() * (val.max - val.min + 1));

        if (val.type === "rand")
            return val.min + Math.random() * (val.max - val.min);

        if (val.type === "deg") {
            const d = evalRuntime(val.degree);
            const o = evalRuntime(val.octave);
            if (Number.isFinite(d) && Number.isFinite(o)) return 12 * o + d;
            return null;
        }

        return val;
    }

    // ------------------ fire osc ------------------
    const start = () => {

        const sample = sampleVisual(el);
        if (!sample) return;

        // --- real pixel geometry ---
        const rect = el.getBoundingClientRect();
        const pxWidth = rect.width || 0;
        const pxHeight = rect.height || 0;
        const pxArea = Math.max(0, pxWidth * pxHeight);

        const values = {};
        for (const [param, source] of Object.entries(visualMappings)) {
            if (sample[source] != null) values[param] = sample[source];
        }

        const evaluatedStatic = {};
        for (const [key, val] of Object.entries(staticParams)) {

            if (key === "pitch" && val?.type === "deg") {
                let deg = evalRuntime(val.degree);
                let oct = evalRuntime(val.octave);

                if (Number.isFinite(deg)) deg = Math.round(deg);
                if (Number.isFinite(oct)) oct = Math.round(oct);

                values.pitchDeg = deg ?? 0;
                values.pitchOct = oct ?? 0;
                continue;
            }

            if (key === "pitch" && val?.type === "hz") {
                values.pitchHz = evalRuntime(val.value);
                continue;
            }

            if (key === "pitch" && val?.type === "midi") {
                values.pitchMidi = evalRuntime(val.value);
                continue;
            }

            evaluatedStatic[key] = evalRuntime(val);
        }

        // -------------------------------
        // POSITIONAL OSC ARGUMENTS
        // pitchType, pitchA, pitchB, size, env, density
        // -------------------------------
        let pitchType = 0;
        let pitchA = 0;
        let pitchB = 0;

        if (values.pitchDeg != null && values.pitchOct != null) {
            pitchType = 1;
            pitchA = values.pitchDeg;
            pitchB = values.pitchOct;
        }
        else if (values.pitchHz != null) {
            pitchType = 2;
            pitchA = values.pitchHz;
        }
        else if (values.pitchMidi != null) {
            pitchType = 3;
            pitchA = values.pitchMidi;
        }
        else if (visualMappings.pitch === "y" && sample.y != null) {
            pitchType = 4;
            pitchA = sample.y;
        }
        // pitch:<number> — a fixed 0–1 control value, sent exactly like pitch:y
        // (pitchType 4), for objects whose pitch should not depend on where they
        // happen to be on screen: a star keeps its note however the view zooms,
        // pans or rotates
        else if (typeof evaluatedStatic.pitch === "number" && Number.isFinite(evaluatedStatic.pitch)) {
            pitchType = 4;
            pitchA = evaluatedStatic.pitch;
        }

        // amp: static override for size (e.g. from midi-to-svg velocity mapping)
        const size = (typeof evaluatedStatic.amp === "number")
            ? evaluatedStatic.amp
            : (values.size ?? sample.size ?? 0);
        const env = values.env ?? values.width ?? sample.width ?? 0;

        // --- area → density normalization (pixels!) ---
        let density;

        if (typeof values.density === "number") {
            density = values.density;
        } else {
            const MIN_AREA = 9;    // ≈ 3×3 px
            const MAX_AREA = 2500;  // ≈ 50×50 px

            density = norm(pxArea, MIN_AREA, MAX_AREA);
        }

        const baseArgs = argList
            ? argList.map((a) => {
                const v = evalRuntime(a);
                if (typeof v === "number") return fmt(v);
                if (typeof v === "string" && Number.isFinite(sample[v])) return fmt(sample[v]);
                return typeof v === "string" ? v.replace(/^["'`]+|["'`]+$/g, "") : v;
            })
            : [pitchType, pitchA, pitchB, fmt(size), fmt(env), fmt(density)];
        if (!argList && staticParams.root != null) baseArgs.push(staticParams.root);
        if (sendPos) {
            const p = samplePosInFrame(el);
            if (p) baseArgs.push(fmt(p.x), fmt(p.y));
        }

        // dur and flashdur are beats: seconds at the tempo of this fire
        const durSecs = dur != null ? secs(dur) : null;
        const flashSecs = flashDur != null ? secs(flashDur) : 0.35;

        // gate: explicit (gate:0/gate:1) takes priority; falls back to dur-based gating
        let gateNow = null;
        let scheduleOff = false;
        if (gate !== null) {
            gateNow = gate;  // explicit gate — no setTimeout
        } else if (durSecs != null && durSecs > 0) {
            gateNow = 1;     // dur-mode: note-on now, note-off after dur
            scheduleOff = true;
        }

        sendOSCMessage({
            type: "osc_value",
            addr,
            args: gateNow !== null ? [...baseArgs, gateNow] : baseArgs,
            timestamp: Date.now()
        });
        if (flash > 0) flashElement(el, flash, flashSecs);

        if (scheduleOff) {
            setTimeout(() => {
                sendOSCMessage({
                    type: "osc_value",
                    addr,
                    args: [...baseArgs, 0],
                    timestamp: Date.now()
                });
            }, durSecs * 1000);
        }
    };



    if (el && needsArming(el)) {
        armElement(el, start);
        return;
    }

    start();
}



///////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////
///////// SHARED FUNCTION FOR OSC GUI "TOOLTIP" OVERLAY
///////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////
// ----------------------------------------------------------------------------
// Global OSC Debug Overlay Factory
// ----------------------------------------------------------------------------
// Usage:
//   const o = createOscOverlay({
//     anchorEl: elOrPath,
//     label: "/fx/ring/freq",
//     anchorMode: "bbox" | "center"   (default = bbox)
//   });
//
//   o.update("val:0.72");   // the box shows the address; values are not shown
//   o.destroy();
// ----------------------------------------------------------------------------

export function createOscOverlay({
    anchorEl,
    label = "OSC",
    track = true,       // auto-track via RAF
    anchorMode = "bbox" // "bbox" | "center"
} = {}) {

    // --------------------------------------------------------
    // Nothing to anchor to → return a no-op stub
    // --------------------------------------------------------
    if (!anchorEl) {
        return {
            update() { },
            position() { },
            startTracking() { },
            stopTracking() { },
            destroy() { }
        };
    }

    // --------------------------------------------------------
    // Create DOM overlay
    // --------------------------------------------------------
    const box = document.createElement("div");
    box.className = "oscilla-overlay";

    box.style.position = "fixed";
    box.style.pointerEvents = "none";
    box.style.zIndex = 99999;

    box.style.padding = "4px 8px";
    box.style.borderRadius = "6px";
    box.style.background = "rgba(0,0,0,.075)";
    box.style.color = "rgba(0, 0, 0, 1)";
    box.style.fontFamily = "monospace";
    box.style.fontSize = "11px";
    box.style.whiteSpace = "nowrap";
    box.style.width = "max-content";       // ← shrink-wrap to text content
    box.style.maxWidth = "300px";           // ← safety cap

    // initial label
    box.textContent = label ?? "";

    document.body.appendChild(box);

    // --------------------------------------------------------
    // Helpers
    // --------------------------------------------------------

    function positionBBox() {
        const r = anchorEl.getBoundingClientRect();
        box.style.left = `${r.left}px`;
        box.style.top = `${r.top}px`;
    }

    // SVG-aware pivot positioning
    function positionCenter() {
        // non-SVG fallback → bbox center
        if (!anchorEl.getBBox || !anchorEl.ownerSVGElement) {
            const r = anchorEl.getBoundingClientRect();
            box.style.left = `${r.left + r.width / 2}px`;
            box.style.top = `${r.top + r.height / 2}px`;
            return;
        }

        const svg = anchorEl.ownerSVGElement;
        const bbox = anchorEl.getBBox();
        const cx = bbox.x + bbox.width / 2;
        const cy = bbox.y + bbox.height / 2;

        const pt = svg.createSVGPoint();
        pt.x = cx;
        pt.y = cy;

        const screen = pt.matrixTransform(anchorEl.getScreenCTM());

        box.style.left = `${screen.x}px`;
        box.style.top = `${screen.y}px`;
    }

    function position() {
        if (!anchorEl || !box.isConnected) return;

        if (anchorMode === "center") {
            positionCenter();
        } else {
            // default behaviour
            positionBBox();
        }
    }

    // --------------------------------------------------------
    // Tracking loop (independent from animations)
    // --------------------------------------------------------
    let tracking = false;

    function loop() {
        if (!tracking) return;
        try { position(); } catch { /* anchor not rendered: getBBox/getScreenCTM unavailable */ }
        requestAnimationFrame(loop);
    }

    function startTracking() {
        if (tracking) return;
        tracking = true;
        requestAnimationFrame(loop);
    }

    function stopTracking() {
        tracking = false;
    }

    if (track) startTracking();

    // --------------------------------------------------------
    // Value updater: the box keeps showing the address
    // --------------------------------------------------------
    function update() { }

    // --------------------------------------------------------
    // Cleanup
    // --------------------------------------------------------
    function destroy() {
        stopTracking();
        box.remove();
    }

    // public API
    return {
        el: box,
        update,
        position,
        startTracking,
        stopTracking,
        destroy
    };
}
